/**
 * Math editor symbol catalogue + category colours.
 * Sourced from the design handoff at:
 *   lilia-design/MathFormulaEditor/design_handoff_math_editor/src/math-shared.jsx
 *
 * Edits to this file should stay in sync with that source. Each item
 * carries the glyph (Unicode), the LaTeX command, and a short name used
 * by the slash command palette search.
 */

export interface SymbolEntry {
  /** Unicode glyph. */
  g: string;
  /** LaTeX command (e.g. `\alpha`). */
  c: string;
  /** Plain-name (searchable). */
  n: string;
}

export type CategoryKey =
  | "greek"
  | "ops"
  | "rel"
  | "bigops"
  | "arrows"
  | "fn"
  | "logic"
  | "sets"
  | "struct";

export interface CategoryColor {
  /** Foreground colour for glyph / category label / focused row rule. */
  ink: string;
  /** 10–12% background tint for tile hover. */
  soft: string;
  /** 22–24% border for hovered tile. */
  mid: string;
  /** Human label for the category column. */
  name: string;
}

// Category colours resolve from CSS custom properties (--cat-<key> in
// global.scss) so they follow the active theme — the .dark block lifts each
// hue for dark/dim/forest. soft/mid tile tints derive via color-mix, matching
// the old ~11% bg / ~23% border alphas. (Claude Design 2026-06-14, §6.2.)
function cat(key: CategoryKey, name: string): CategoryColor {
  const v = `var(--cat-${key})`;
  return {
    ink: v,
    soft: `color-mix(in srgb, ${v} 11%, transparent)`,
    mid: `color-mix(in srgb, ${v} 23%, transparent)`,
    name,
  };
}

export const CAT_COLORS: Record<CategoryKey, CategoryColor> = {
  greek:  cat("greek", "Greek"),
  ops:    cat("ops", "Operators"),
  rel:    cat("rel", "Relations"),
  bigops: cat("bigops", "Big ops"),
  arrows: cat("arrows", "Arrows"),
  fn:     cat("fn", "Functions"),
  logic:  cat("logic", "Logic"),
  sets:   cat("sets", "Sets"),
  struct: cat("struct", "Structures"),
};

export const PAL: Record<Exclude<CategoryKey, "struct">, SymbolEntry[]> = {
  greek: [
    { g: "α", c: "\\alpha", n: "alpha" },
    { g: "β", c: "\\beta", n: "beta" },
    { g: "γ", c: "\\gamma", n: "gamma" },
    { g: "δ", c: "\\delta", n: "delta" },
    { g: "ε", c: "\\varepsilon", n: "epsilon" },
    { g: "ζ", c: "\\zeta", n: "zeta" },
    { g: "η", c: "\\eta", n: "eta" },
    { g: "θ", c: "\\theta", n: "theta" },
    { g: "ι", c: "\\iota", n: "iota" },
    { g: "κ", c: "\\kappa", n: "kappa" },
    { g: "λ", c: "\\lambda", n: "lambda" },
    { g: "μ", c: "\\mu", n: "mu" },
    { g: "ν", c: "\\nu", n: "nu" },
    { g: "ξ", c: "\\xi", n: "xi" },
    { g: "π", c: "\\pi", n: "pi" },
    { g: "ρ", c: "\\rho", n: "rho" },
    { g: "σ", c: "\\sigma", n: "sigma" },
    { g: "τ", c: "\\tau", n: "tau" },
    { g: "φ", c: "\\varphi", n: "phi" },
    { g: "χ", c: "\\chi", n: "chi" },
    { g: "ψ", c: "\\psi", n: "psi" },
    { g: "ω", c: "\\omega", n: "omega" },
    { g: "Γ", c: "\\Gamma", n: "Gamma" },
    { g: "Δ", c: "\\Delta", n: "Delta" },
    { g: "Θ", c: "\\Theta", n: "Theta" },
    { g: "Λ", c: "\\Lambda", n: "Lambda" },
    { g: "Π", c: "\\Pi", n: "Pi" },
    { g: "Σ", c: "\\Sigma", n: "Sigma" },
    { g: "Φ", c: "\\Phi", n: "Phi" },
    { g: "Ψ", c: "\\Psi", n: "Psi" },
    { g: "Ω", c: "\\Omega", n: "Omega" },
    { g: "ℓ", c: "\\ell", n: "ell script l" },
  ],
  ops: [
    { g: "×", c: "\\times", n: "times" },
    { g: "÷", c: "\\div", n: "div" },
    { g: "⋅", c: "\\cdot", n: "cdot" },
    { g: "±", c: "\\pm", n: "pm" },
    { g: "∓", c: "\\mp", n: "mp" },
    { g: "⊕", c: "\\oplus", n: "oplus" },
    { g: "⊗", c: "\\otimes", n: "otimes" },
    { g: "⊙", c: "\\odot", n: "odot" },
    { g: "∗", c: "\\ast", n: "ast" },
    { g: "∘", c: "\\circ", n: "circ" },
    { g: "⋆", c: "\\star", n: "star" },
    { g: "∂", c: "\\partial", n: "partial" },
    { g: "∇", c: "\\nabla", n: "nabla" },
    { g: "∆", c: "\\Delta", n: "laplacian" },
    // Constants / dots / logic + set ops the parser classifies as `op`
    // (latexToTokens OPS/LOGIC → T.op) — added so they're insertable and
    // the build generator can click them in the `ops` category.
    { g: "∞", c: "\\infty", n: "infinity" },
    { g: "ℏ", c: "\\hbar", n: "hbar planck" },
    { g: "⋯", c: "\\cdots", n: "cdots dots" },
    { g: "…", c: "\\ldots", n: "ldots dots" },
    { g: "⋮", c: "\\vdots", n: "vdots dots" },
    { g: "⋱", c: "\\ddots", n: "ddots dots" },
    { g: "∀", c: "\\forall", n: "forall" },
    { g: "∃", c: "\\exists", n: "exists" },
    { g: "∪", c: "\\cup", n: "union" },
    { g: "∩", c: "\\cap", n: "intersection" },
    { g: "∖", c: "\\setminus", n: "setminus" },
  ],
  rel: [
    { g: "=", c: "=", n: "equals" },
    { g: "≠", c: "\\neq", n: "neq" },
    { g: "≈", c: "\\approx", n: "approx" },
    { g: "≡", c: "\\equiv", n: "equiv" },
    { g: "∼", c: "\\sim", n: "sim" },
    { g: "≃", c: "\\simeq", n: "simeq" },
    { g: "≅", c: "\\cong", n: "cong" },
    { g: "∝", c: "\\propto", n: "propto" },
    { g: "≤", c: "\\leq", n: "leq" },
    { g: "≥", c: "\\geq", n: "geq" },
    { g: "≪", c: "\\ll", n: "ll" },
    { g: "≫", c: "\\gg", n: "gg" },
    { g: "⊂", c: "\\subset", n: "subset" },
    { g: "⊆", c: "\\subseteq", n: "subseteq" },
    { g: "⊃", c: "\\supset", n: "supset" },
    { g: "∈", c: "\\in", n: "in" },
    { g: "∉", c: "\\notin", n: "notin" },
    { g: "∋", c: "\\ni", n: "ni" },
  ],
  bigops: [
    { g: "∫", c: "\\int", n: "integral" },
    { g: "∬", c: "\\iint", n: "double int" },
    { g: "∭", c: "\\iiint", n: "triple int" },
    { g: "∮", c: "\\oint", n: "contour" },
    { g: "∑", c: "\\sum", n: "sum" },
    { g: "∏", c: "\\prod", n: "product" },
    { g: "∐", c: "\\coprod", n: "coproduct" },
    { g: "⋃", c: "\\bigcup", n: "union" },
    { g: "⋂", c: "\\bigcap", n: "intersection" },
    { g: "⋁", c: "\\bigvee", n: "bigvee" },
    { g: "⋀", c: "\\bigwedge", n: "bigwedge" },
    { g: "⨁", c: "\\bigoplus", n: "bigoplus" },
  ],
  arrows: [
    { g: "←", c: "\\leftarrow", n: "left" },
    { g: "→", c: "\\rightarrow", n: "right" },
    { g: "↔", c: "\\leftrightarrow", n: "leftright" },
    { g: "⇐", c: "\\Leftarrow", n: "Left implies" },
    { g: "⇒", c: "\\Rightarrow", n: "implies" },
    { g: "⇔", c: "\\Leftrightarrow", n: "iff" },
    { g: "↦", c: "\\mapsto", n: "mapsto" },
    { g: "↪", c: "\\hookrightarrow", n: "inject" },
    { g: "↠", c: "\\twoheadrightarrow", n: "surject" },
    { g: "↗", c: "\\nearrow", n: "nearrow" },
    { g: "↘", c: "\\searrow", n: "searrow" },
    { g: "⟶", c: "\\longrightarrow", n: "long right" },
  ],
  fn: [
    { g: "sin",  c: "\\sin",  n: "sin" },
    { g: "cos",  c: "\\cos",  n: "cos" },
    { g: "tan",  c: "\\tan",  n: "tan" },
    { g: "log",  c: "\\log",  n: "log" },
    { g: "ln",   c: "\\ln",   n: "ln" },
    { g: "exp",  c: "\\exp",  n: "exp" },
    { g: "lim",  c: "\\lim",  n: "lim" },
    { g: "inf",  c: "\\inf",  n: "inf" },
    { g: "sup",  c: "\\sup",  n: "sup" },
    { g: "max",  c: "\\max",  n: "max" },
    { g: "min",  c: "\\min",  n: "min" },
    { g: "det",  c: "\\det",  n: "det" },
    { g: "arg",  c: "\\arg",  n: "arg" },
    { g: "gcd",  c: "\\gcd",  n: "gcd" },
  ],
  logic: [
    { g: "∀", c: "\\forall", n: "forall" },
    { g: "∃", c: "\\exists", n: "exists" },
    { g: "∄", c: "\\nexists", n: "nexists" },
    { g: "¬", c: "\\neg", n: "neg" },
    { g: "∧", c: "\\wedge", n: "wedge" },
    { g: "∨", c: "\\vee", n: "vee" },
    { g: "⊢", c: "\\vdash", n: "vdash" },
    { g: "⊨", c: "\\models", n: "models" },
    { g: "⊥", c: "\\bot", n: "bot" },
    { g: "⊤", c: "\\top", n: "top" },
  ],
  sets: [
    { g: "ℕ", c: "\\mathbb{N}", n: "naturals" },
    { g: "ℤ", c: "\\mathbb{Z}", n: "integers" },
    { g: "ℚ", c: "\\mathbb{Q}", n: "rationals" },
    { g: "ℝ", c: "\\mathbb{R}", n: "reals" },
    { g: "ℂ", c: "\\mathbb{C}", n: "complex" },
    { g: "∅", c: "\\emptyset", n: "empty" },
    { g: "∪", c: "\\cup", n: "cup" },
    { g: "∩", c: "\\cap", n: "cap" },
    { g: "∖", c: "\\setminus", n: "setminus" },
    { g: "𝒫", c: "\\mathcal{P}", n: "powerset" },
    { g: "ℵ", c: "\\aleph", n: "aleph" },
  ],
};

const LOW_GREEK = new Set("αβγδεζηθικλμνξπρστφχψω".split(""));

export function isLowercaseGreek(g: string): boolean {
  return LOW_GREEK.has(g);
}

/** Build a flat list of (category, entry) pairs for the slash palette. */
export interface SlashItem {
  cat: CategoryKey;
  col: CategoryColor;
  entry: SymbolEntry;
}

/**
 * Structures the slash palette can expand (e.g. `\frac` → a fraction with
 * empty slots). `c` is the LaTeX command searched/matched; SlashPalette maps
 * it to the right structure-token constructor. Listed first so they surface at
 * the top of the no-query palette.
 */
export const STRUCT_ENTRIES: SymbolEntry[] = [
  { g: "▦", c: "\\frac", n: "fraction" },
  { g: "√", c: "\\sqrt", n: "square root" },
  { g: "ⁿ√", c: "\\sqrt[n]", n: "nth root" },
  { g: "∫", c: "\\int", n: "integral" },
  { g: "∑", c: "\\sum", n: "sum series" },
  { g: "lim", c: "\\lim", n: "limit" },
  { g: "(ⁿₖ)", c: "\\binom", n: "binomial coefficient choose" },
  { g: "[▦]", c: "\\matrix", n: "matrix" },
  { g: "(▦)", c: "\\pmatrix", n: "matrix parentheses" },
  { g: "[▦]", c: "\\bmatrix", n: "matrix brackets" },
  { g: "{", c: "\\cases", n: "cases piecewise" },
  { g: "≡", c: "\\aligned", n: "aligned equations" },
  { g: "v⃗", c: "\\colvec", n: "column vector" },
  { g: "x⃗", c: "\\vec", n: "vector accent" },
  { g: "x̂", c: "\\hat", n: "hat accent" },
  { g: "x̄", c: "\\bar", n: "bar accent" },
  { g: "x̃", c: "\\tilde", n: "tilde accent" },
  { g: "x̅", c: "\\overline", n: "overline" },
  { g: "ẋ", c: "\\dot", n: "dot accent derivative" },
  { g: "|▦|", c: "\\abs", n: "absolute value" },
];

export function buildSlashIndex(): SlashItem[] {
  const out: SlashItem[] = [];
  // Structures first — `\frac`, `\sqrt`, `\matrix`, … expand into slots.
  for (const entry of STRUCT_ENTRIES) {
    out.push({ cat: "struct", col: CAT_COLORS.struct, entry });
  }
  (Object.keys(PAL) as Array<keyof typeof PAL>).forEach((k) => {
    const col = CAT_COLORS[k];
    for (const entry of PAL[k]) {
      out.push({ cat: k, col, entry });
    }
  });
  return out;
}

/** Fuzzy score against a normalized query (no `\` prefix, lowercase). */
export function matchScore(item: SlashItem, q: string): number {
  if (!q) return 1;
  const cmd = item.entry.c.replace(/^\\/, "").toLowerCase();
  const name = item.entry.n.toLowerCase();
  if (cmd === q) return 100;
  if (cmd.startsWith(q)) return 60;
  if (name.startsWith(q)) return 40;
  if (cmd.includes(q)) return 20;
  if (name.includes(q)) return 10;
  return 0;
}
